import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Apple, Play } from "lucide-react";
import { VentLogo } from "./VentLogo";
import { getSocialMediaLinks } from "./SocialMediaSettings";
import { openExternalUrl } from "@/hooks/useExternalLink";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { APP_STORE_URL, PLAY_STORE_URL, shouldShowAppStore, shouldShowPlayStore } from "@/lib/storeLinks";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const linkClass =
  "text-sm text-muted-foreground transition-colors hover:text-foreground";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isNative = useNativePlatform();
  const [socialLinks, setSocialLinks] = useState(getSocialMediaLinks());

  useEffect(() => {
    const updateLinks = () => setSocialLinks(getSocialMediaLinks());
    window.addEventListener("storage", updateLinks);
    window.addEventListener("social-media-updated", updateLinks);
    return () => {
      window.removeEventListener("storage", updateLinks);
      window.removeEventListener("social-media-updated", updateLinks);
    };
  }, []);

  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-10 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <VentLogo />
            <p className="mt-4 max-w-xs font-display uppercase text-xl leading-tight tracking-tight text-foreground">
              Designed to help you let it out
            </p>
            <div className="mt-6 flex gap-5">
              <button
                onClick={() => openExternalUrl(socialLinks.instagram)}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </button>
              <button
                onClick={() => openExternalUrl(socialLinks.facebook)}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </button>
              <button
                onClick={() => openExternalUrl(socialLinks.x)}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="X"
              >
                <XIcon className="h-5 w-5" />
              </button>
              {socialLinks.tiktok && (
                <button
                  onClick={() => openExternalUrl(socialLinks.tiktok)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="h-5 w-5" />
                </button>
              )}
              {socialLinks.linkedin && (
                <button
                  onClick={() => openExternalUrl(socialLinks.linkedin)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Explore */}
          <nav aria-label="Explore">
            <h2 className="font-display uppercase text-xs tracking-[0.3em] text-slate">Explore</h2>
            <ul className="mt-5 space-y-3">
              <li>
                <Link to="/about" className={linkClass}>
                  About
                </Link>
              </li>
              <li>
                <button onClick={() => openExternalUrl("https://theventapp.myshopify.com")} className={linkClass}>
                  Store
                </button>
              </li>
              <li>
                <Link to="/partnerships" className={linkClass}>
                  Partnerships
                </Link>
              </li>
              <li>
                <Link to="/anonymous-vent-app" className={linkClass}>
                  Anonymous Vent App
                </Link>
              </li>
              <li>
                <Link to="/resources" className={linkClass}>
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/faq" className={linkClass}>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className={linkClass}>
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Legal">
            <h2 className="font-display uppercase text-xs tracking-[0.3em] text-slate">Legal</h2>
            <ul className="mt-5 space-y-3">
              <li>
                <Link to="/privacy-policy" className={linkClass}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className={linkClass}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/dmca" className={linkClass}>
                  DMCA
                </Link>
              </li>
            </ul>
          </nav>

          {/* Get the app */}
          <div>
            <h2 className="font-display uppercase text-xs tracking-[0.3em] text-slate">
              Get the app
            </h2>
            {!isNative && shouldShowAppStore() && (
              <button
                onClick={() => openExternalUrl(APP_STORE_URL)}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-foreground transition-transform duration-300 hover:-translate-y-0.5"
              >
                <Apple className="h-4 w-4" />
                App Store
              </button>
            )}
            {!isNative && shouldShowPlayStore() && (
              <button
                onClick={() => openExternalUrl(PLAY_STORE_URL)}
                className="mt-3 ml-0 sm:ml-3 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-foreground transition-transform duration-300 hover:-translate-y-0.5"
              >
                <Play className="h-4 w-4" />
                Google Play
              </button>
            )}
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">© {currentYear} Vent. All rights reserved.</p>
          <p className="text-sm text-muted-foreground">
            In crisis? Call or text 988 (US) for immediate support.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
